// Port of Networking/HTTPTypes.swift: transport-agnostic request/response types and the HTTPClient interface.
// Durations are seconds (Swift `TimeInterval`). Bodies are bytes (`Uint8Array`, Swift `Data`).

import { NetworkFailure } from './networkError';
import type { RetryPolicy } from './retryPolicy';

/**
 * How a request may use the browser's HTTP cache. Mapped to `fetch`'s `cache` option by `FetchHTTPClient`:
 * useProtocolCachePolicy → `default`, reloadIgnoringCache → `no-store`, returnCacheElseLoad → `force-cache`
 * (`only-if-cached` while offline), cacheOnly → `only-if-cached` (never retried).
 */
export type HTTPCachePolicy = 'useProtocolCachePolicy' | 'reloadIgnoringCache' | 'returnCacheElseLoad' | 'cacheOnly';

export const allHTTPCachePolicies: readonly HTTPCachePolicy[] = [
  'useProtocolCachePolicy',
  'reloadIgnoringCache',
  'returnCacheElseLoad',
  'cacheOnly',
];

/** Whether a request with this policy may still be answered from the cache while the device is offline. */
export function canBeServedOffline(policy: HTTPCachePolicy): boolean {
  return policy === 'returnCacheElseLoad' || policy === 'cacheOnly';
}

/** `fetch` cache mode for a policy, depending on whether the device is known to be offline. */
export function fetchCacheMode(policy: HTTPCachePolicy, isOffline: boolean): RequestCache {
  switch (policy) {
    case 'useProtocolCachePolicy':
      return 'default';
    case 'reloadIgnoringCache':
      return 'no-store';
    case 'returnCacheElseLoad':
      return isOffline ? 'only-if-cached' : 'force-cache';
    case 'cacheOnly':
      return 'only-if-cached';
  }
}

export interface HTTPRequest {
  /** Absolute `http`/`https` URL, or a path resolved against the client's base URL (the app's origin). */
  url: string;
  /** HTTP method, e.g. `GET`. Sent upper-cased. */
  method: string;
  /** Header fields. Browsers do not let a page set `User-Agent`; `FetchHTTPClient` drops it. */
  headers: Record<string, string>;
  body?: Uint8Array | string;
  /** Per-attempt timeout, seconds. */
  timeout: number;
  cachePolicy: HTTPCachePolicy;
  /** Retry behaviour for this request; undefined uses the client's policy. */
  retryPolicy?: RetryPolicy;
  /** Aborting it cancels the request (Swift task cancellation); never retried. */
  signal?: AbortSignal;
  /** Cookies: `omit` (default — requests carry no identifiers), or `same-origin` for the app's own API. */
  credentials?: RequestCredentials;
}

/** Request with the Swift defaults: GET, no headers, 20 s timeout, protocol cache policy. */
export function makeHTTPRequest(url: string, init: Partial<Omit<HTTPRequest, 'url'>> = {}): HTTPRequest {
  return {
    url,
    method: init.method ?? 'GET',
    headers: init.headers ?? {},
    body: init.body,
    timeout: init.timeout ?? 20,
    cachePolicy: init.cachePolicy ?? 'useProtocolCachePolicy',
    retryPolicy: init.retryPolicy,
    signal: init.signal,
    credentials: init.credentials,
  };
}

export function normalizedMethod(request: HTTPRequest): string {
  const trimmed = request.method.trim();
  return trimmed === '' ? 'GET' : trimmed.toUpperCase();
}

/** Methods that can be repeated without additional side effects (RFC 9110 §9.2.2). */
export function isIdempotent(request: HTTPRequest): boolean {
  return ['GET', 'HEAD', 'OPTIONS', 'TRACE', 'PUT', 'DELETE'].includes(normalizedMethod(request));
}

const conditionalHeaderNames = ['if-modified-since', 'if-none-match'];

/** Whether the caller sent its own validator, so that `304 Not Modified` is an expected answer. */
export function isConditional(request: HTTPRequest): boolean {
  return Object.entries(request.headers).some(
    ([name, value]) => value !== '' && conditionalHeaderNames.includes(name.toLowerCase()),
  );
}

/** Whether `status` counts as success: any 2xx, and 304 for a conditional request. */
export function acceptsStatus(request: HTTPRequest, status: number): boolean {
  return (status >= 200 && status < 300) || (status === 304 && isConditional(request));
}

/**
 * A successful response: any 2xx, or `304 Not Modified` answering a conditional request (empty body; the caller
 * reuses its stored copy). Every other status is thrown as `NetworkError` `http`.
 */
export interface HTTPResponse {
  data: Uint8Array;
  statusCode: number;
  /** Header fields as received (fetch lower-cases names). Use `headerValue` for case-insensitive lookup. */
  headers: Record<string, string>;
  /** Final URL after redirects. */
  url: string;
}

export function makeHTTPResponse(init: {
  url: string;
  data?: Uint8Array | string;
  statusCode?: number;
  headers?: Record<string, string>;
}): HTTPResponse {
  const data = typeof init.data === 'string' ? new TextEncoder().encode(init.data) : (init.data ?? new Uint8Array());
  return { data, statusCode: init.statusCode ?? 200, headers: init.headers ?? {}, url: init.url };
}

/** Case-insensitive header lookup. */
export function headerValue(response: HTTPResponse, name: string): string | undefined {
  const exact = response.headers[name];
  if (exact !== undefined) return exact;
  const lower = name.toLowerCase();
  for (const [key, value] of Object.entries(response.headers)) if (key.toLowerCase() === lower) return value;
  return undefined;
}

/** The body as UTF-8 text. */
export function responseText(response: HTTPResponse): string {
  return new TextDecoder().decode(response.data);
}

/**
 * Decodes the body as JSON, then through `decode` (a validator that throws with a diagnostic, e.g. a missing key and
 * its path). Throws `NetworkFailure` `decoding` on failure.
 */
export function decodeJSON<T = unknown>(response: HTTPResponse, decode?: (json: unknown) => T): T {
  let json: unknown;
  try {
    json = JSON.parse(responseText(response));
  } catch (error) {
    throw new NetworkFailure({ kind: 'decoding', message: `Corrupted data at <root>: ${String(error)}` });
  }
  if (!decode) return json as T;
  try {
    return decode(json);
  } catch (error) {
    throw new NetworkFailure({ kind: 'decoding', message: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Minimal async HTTP abstraction used by every service (weather, geo.admin.ch…). Implementations throw
 * `NetworkFailure` only and treat non-2xx statuses as `http` errors — except 304 for a conditional request.
 */
export interface HTTPClient {
  send(request: HTTPRequest): Promise<HTTPResponse>;
}

/** GETs `url` and returns the body. */
export async function getData(client: HTTPClient, url: string, timeout = 20): Promise<Uint8Array> {
  return (await client.send(makeHTTPRequest(url, { timeout }))).data;
}

/** GETs `url` with `Accept: application/json` and decodes the body (see `decodeJSON`). */
export async function getJSON<T = unknown>(client: HTTPClient, url: string, decode?: (json: unknown) => T): Promise<T> {
  const response = await client.send(makeHTTPRequest(url, { headers: { Accept: 'application/json' } }));
  return decodeJSON(response, decode);
}
